/**
 * Hook: usePublishedContracts
 * Descrição: CRUD completo de contratos publicados (entidade configurável)
 * 
 * RELEASE: v1.0.0 - 2026-02-19
 * Funcionalidades:
 * - Listar contratos do tenant (com filtros opcionais)
 * - Criar novo contrato publicado
 * - Atualizar contrato existente
 * - Deletar contrato
 * - Filtrar por status e workspace
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { createLogger } from '@/lib/logger';
import type { PublishedContract, PublishedContractInput } from '@/types/contracts';

// Logger do módulo
const logger = createLogger('PublishedContracts');

// Chave de query base
const QUERY_KEY = 'published-contracts';

/**
 * Hook principal para gerenciamento de contratos publicados
 */
export function usePublishedContracts() {
  const { currentTenant } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id;

  // =========================================================================
  // Query: Listar todos os contratos publicados do tenant
  // =========================================================================
  const {
    data: publishedContracts = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [QUERY_KEY, tenantId],
    queryFn: async () => {
      if (!tenantId) return [];

      logger.debug('Buscando contratos publicados', { tenantId });

      const { data, error } = await supabase
        .from('published_contracts')
        .select(`
          *,
          contract_templates (
            id, name, category, description,
            contract_template_versions (version_number)
          ),
          contract_template_versions (
            id, version_number
          ),
          tenant_signature_configs (
            id, is_default, is_active,
            signature_providers (
              id, name, display_name, slug
            )
          )
        `)
        .eq('tenant_id', tenantId)
        .order('updated_at', { ascending: false });

      if (error) {
        logger.error('Erro ao buscar contratos publicados', { error });
        throw error;
      }

      logger.info('Contratos publicados carregados', { count: data?.length });
      return (data || []) as unknown as PublishedContract[];
    },
    enabled: !!tenantId,
  });

  // =========================================================================
  // Query: Listar apenas contratos em produção (para o wizard)
  // =========================================================================
  const {
    data: productionContracts = [],
    isLoading: isLoadingProduction,
  } = useQuery({
    queryKey: [QUERY_KEY, tenantId, 'production'],
    queryFn: async () => {
      if (!tenantId) return [];

      logger.debug('Buscando contratos em produção', { tenantId });

      const { data, error } = await supabase
        .from('published_contracts')
        .select(`
          *,
          contract_templates (
            id, name, category, description, content_html, variables,
            background_image_path, bg_margin_top_mm, bg_margin_bottom_mm,
            show_page_numbers, page_number_position, pagination_y_offset_mm,
            base_pdf_path, cover_pdf_path, base_pdf_position,
            source_type, source_pdf_storage_path,
            contract_template_versions (version_number)
          ),
          contract_template_versions (
            id, version_number, content_html, variables,
            background_image_path, bg_margin_top_mm, bg_margin_bottom_mm,
            show_page_numbers, page_number_position, pagination_y_offset_mm,
            base_pdf_path, cover_pdf_path, base_pdf_position,
            source_type, source_pdf_storage_path
          ),
          tenant_signature_configs (
            id, is_default, is_active, credentials,
            signature_providers (
              id, name, display_name, slug
            )
          )
        `)
        .eq('tenant_id', tenantId)
        .eq('status', 'production')
        .order('name', { ascending: true });

      if (error) {
        logger.error('Erro ao buscar contratos em produção', { error });
        throw error;
      }

      logger.info('Contratos em produção carregados', { count: data?.length });
      return (data || []) as unknown as PublishedContract[];
    },
    enabled: !!tenantId,
  });

  // =========================================================================
  // Mutation: Criar novo contrato publicado
  // =========================================================================
  const createPublishedContract = useMutation({
    mutationFn: async (input: PublishedContractInput) => {
      if (!tenantId) {
        throw new Error('Tenant não identificado');
      }

      logger.info('Criando contrato publicado', { name: input.name });

      const { data: userData } = await supabase.auth.getUser();

      const { data, error } = await supabase
        .from('published_contracts')
        .insert({
          tenant_id: tenantId,
          name: input.name,
          description: input.description || null,
          template_id: input.template_id,
          template_version_id: input.template_version_id || null,
          signature_config_id: input.signature_config_id || null,
          status: input.status || 'draft',
          allowed_delivery_methods: input.allowed_delivery_methods || ['email'],
          require_selfie: input.require_selfie ?? false,
          workspace_ids: input.workspace_ids || [],
          default_signers: JSON.parse(JSON.stringify(input.default_signers || [])),
          created_by: userData?.user?.id || null,
        } as any)
        .select()
        .single();

      if (error) {
        logger.error('Erro ao criar contrato publicado', { error });
        throw error;
      }

      return data as unknown as PublishedContract;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, tenantId] });
      toast({
        title: 'Contrato criado',
        description: 'O contrato foi criado com sucesso.',
      });
    },
    onError: (error: Error) => {
      logger.error('Falha ao criar contrato publicado', { error: error.message });
      toast({
        title: 'Erro ao criar contrato',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // =========================================================================
  // Mutation: Atualizar contrato publicado existente
  // =========================================================================
  const updatePublishedContract = useMutation({
    mutationFn: async ({ id, ...input }: PublishedContractInput & { id: string }) => {
      logger.info('Atualizando contrato publicado', { id });

      // Tipo seguro do Supabase para update de contratos publicados
      const updateData: Database['public']['Tables']['published_contracts']['Update'] = {};
      if (input.name !== undefined) updateData.name = input.name;
      if (input.description !== undefined) updateData.description = input.description;
      if (input.template_id !== undefined) updateData.template_id = input.template_id;
      // Permite null explícito para zerar a versão fixada (= versão mais recente)
      if ('template_version_id' in input) updateData.template_version_id = input.template_version_id ?? null;
      if (input.signature_config_id !== undefined) updateData.signature_config_id = input.signature_config_id;
      if (input.status !== undefined) updateData.status = input.status;
      if (input.allowed_delivery_methods !== undefined) updateData.allowed_delivery_methods = input.allowed_delivery_methods;
      if (input.require_selfie !== undefined) updateData.require_selfie = input.require_selfie;
      if (input.workspace_ids !== undefined) updateData.workspace_ids = input.workspace_ids;
      if (input.default_signers !== undefined) updateData.default_signers = JSON.parse(JSON.stringify(input.default_signers));

      const { data, error } = await supabase
        .from('published_contracts')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        logger.error('Erro ao atualizar contrato publicado', { error });
        throw error;
      }

      return data as unknown as PublishedContract;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, tenantId] });
      toast({
        title: 'Contrato atualizado',
        description: 'O contrato foi atualizado com sucesso.',
      });
    },
    onError: (error: Error) => {
      logger.error('Falha ao atualizar contrato publicado', { error: error.message });
      toast({
        title: 'Erro ao atualizar contrato',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // =========================================================================
  // Mutation: Deletar contrato publicado
  // =========================================================================
  const deletePublishedContract = useMutation({
    mutationFn: async (id: string) => {
      logger.info('Deletando contrato publicado', { id });

      const { error } = await supabase
        .from('published_contracts')
        .delete()
        .eq('id', id);

      if (error) {
        logger.error('Erro ao deletar contrato publicado', { error });
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, tenantId] });
      toast({
        title: 'Contrato excluído',
        description: 'O contrato foi excluído com sucesso.',
      });
    },
    onError: (error: Error) => {
      logger.error('Falha ao deletar contrato publicado', { error: error.message });
      toast({
        title: 'Erro ao excluir contrato',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    publishedContracts,
    productionContracts,
    isLoading,
    isLoadingProduction,
    error,
    refetch,
    createPublishedContract,
    updatePublishedContract,
    deletePublishedContract,
  };
}
